import { useState, useEffect, useMemo } from 'react';
import { athleteApi, userApi, teamApi } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Loader2, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wave 3 — Athlete transfer modal.
 * - Super Admin / Federation: can change BOTH team and professor.
 * - Team (president): can only change the professor INSIDE their team.
 * The modal validates the chosen professor belongs to the chosen team.
 */
export default function TransferAthleteDialog({ athlete, open, onOpenChange, currentUser, onDone }) {
  const [teams, setTeams] = useState([]);
  const [professors, setProfessors] = useState([]);
  const [teamId, setTeamId] = useState('');
  const [professorId, setProfessorId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loadingLists, setLoadingLists] = useState(false);

  const role = currentUser?.role;
  const canChangeTeam = role === 'super_admin' || role === 'federation';

  useEffect(() => {
    if (!open) return;
    setTeamId(athlete?.team_id || '');
    setProfessorId(athlete?.professor_id || '');
    setReason('');
    setLoadingLists(true);
    Promise.all([teamApi.list(), userApi.list({ role: 'professor' })])
      .then(([t, p]) => { setTeams(t.data || []); setProfessors(p.data || []); })
      .catch(() => toast.error('Erro ao carregar listas'))
      .finally(() => setLoadingLists(false));
  }, [open, athlete]);

  // Professors that belong to the currently-selected team
  const availableProfessors = useMemo(() => {
    const t = teams.find((x) => x._id === teamId);
    if (!t) return professors;
    const ids = new Set([
      ...(t.professor_ids || []),
      t.president_id,
      t.professor_id, // legacy
      ...(t.monitors || []),
    ].filter(Boolean));
    return professors.filter((p) => ids.has(p._id));
  }, [teams, professors, teamId]);

  const teamChanged = teamId !== (athlete?.team_id || '');
  const profChanged = professorId !== (athlete?.professor_id || '');
  const nothingChanged = !teamChanged && !profChanged;

  const handleSubmit = async () => {
    if (nothingChanged) {
      toast.info('Nenhuma alteracao para salvar');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {};
      if (teamChanged) payload.team_id = teamId;
      if (profChanged) payload.professor_id = professorId;
      if (reason.trim()) payload.reason = reason.trim();

      await athleteApi.transfer(athlete._id, payload);
      toast.success('Transferencia registrada com auditoria');
      if (onDone) await onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao transferir atleta');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#141414] border-[#27272A] max-w-md" data-testid="transfer-athlete-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-['Chivo'] flex items-center gap-2">
            <ArrowRightLeft size={16} className="text-[#D4AF37]" /> Transferir Atleta
          </DialogTitle>
          <DialogDescription className="text-[#71717A] text-xs">
            Move o atleta entre equipes/professores. A operação é registrada na auditoria com motivo e executor.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="bg-[#1A1A1A] border border-[#27272A] rounded-md p-3 text-sm">
            <p className="text-white font-semibold">{athlete?.name}</p>
            <p className="text-[#71717A] text-xs mt-1">A transferência será registrada na auditoria com seu usuário, motivo e data.</p>
          </div>

          {!canChangeTeam && (
            <div className="flex items-start gap-2 text-xs text-yellow-400 bg-yellow-500/5 border border-yellow-500/20 rounded p-2">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <span>Como Presidente, você só pode trocar o professor dentro da própria equipe. Para mover entre equipes, peça a Federação ou Super Admin.</span>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-[#A1A1AA]">Equipe</Label>
            <Select value={teamId || 'none'} disabled={!canChangeTeam || loadingLists} onValueChange={(v) => { setTeamId(v === 'none' ? '' : v); setProfessorId(''); }}>
              <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white" data-testid="transfer-team-select">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A] max-h-64">
                <SelectItem value="none" className="text-white">— Nenhuma —</SelectItem>
                {teams.map((t) => (
                  <SelectItem key={t._id} value={t._id} className="text-white">{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-[#A1A1AA]">Professor (sensei)</Label>
            <Select value={professorId || 'none'} disabled={loadingLists} onValueChange={(v) => setProfessorId(v === 'none' ? '' : v)}>
              <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-white" data-testid="transfer-prof-select">
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A] max-h-64">
                <SelectItem value="none" className="text-white">— Nenhum —</SelectItem>
                {availableProfessors.map((p) => (
                  <SelectItem key={p._id} value={p._id} className="text-white">{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {teamId && availableProfessors.length === 0 && (
              <p className="text-xs text-yellow-400">Esta equipe ainda não tem professores cadastrados.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-[#A1A1AA]">Motivo (opcional, mas recomendado)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Mudança de cidade, indicação técnica, evolução de faixa..."
              rows={3}
              data-testid="transfer-reason"
              className="bg-[#1A1A1A] border-[#27272A] text-white"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#27272A]">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#27272A] text-[#A1A1AA]">
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={submitting || loadingLists || nothingChanged} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="transfer-confirm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Transferir'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
